import type { Tone } from './pulse-readings'

/**
 * The judgements the token page makes, apart from the markup.
 *
 * The page it replaces showed a row of grey label-and-value pairs, and so do
 * both the terminals it is measured against. The trouble with that is not that
 * it is ugly — it is that every figure looks equally important, so none of
 * them says anything. What is here turns the two readings that carry a verdict
 * into one, and leaves the rest to be printed plainly.
 */

export type Verdict = {
  word: string
  tone: Tone
  /** The sentence under it, which is the part somebody acts on. */
  detail: string
}

export type AuditCounts = {
  overall: string
  safeCount: number
  warningCount: number
  dangerCount: number
}

/**
 * What the contract checks came to, as a verdict rather than a fraction.
 *
 * "5/7" is the shape both competitors use and it answers nothing: it does not
 * say which two, or whether they matter. A danger is a danger however many
 * checks passed beside it, so the count that leads is the one that is wrong.
 */
export const auditVerdict = (audit: AuditCounts | undefined): Verdict => {
  if (!audit) {
    return {
      word: 'Unchecked',
      tone: 'quiet',
      detail: 'The contract has not been read yet.',
    }
  }

  const total = audit.safeCount + audit.warningCount + audit.dangerCount

  if (total === 0) {
    return {
      word: 'Unchecked',
      tone: 'quiet',
      detail: 'No check has returned for this contract.',
    }
  }

  if (audit.dangerCount > 0) {
    return {
      word: 'Unsafe',
      tone: 'bad',
      detail: `${audit.dangerCount} of ${total} checks failed outright.`,
    }
  }

  if (audit.warningCount > 0) {
    return {
      word: 'Caution',
      tone: 'watch',
      detail: `${audit.warningCount} of ${total} checks came back uneasy; none failed.`,
    }
  }

  return {
    word: 'Clear',
    tone: 'good',
    detail: `All ${total} checks passed.`,
  }
}

/**
 * How much of the market cap is actually behind it, as a percentage.
 *
 * The figure that decides whether a position can be got out of. A million-
 * dollar cap on eight thousand of liquidity is not a million-dollar token, and
 * neither terminal says so — they print both numbers and leave the division to
 * somebody doing it in their head while the price moves.
 */
export const liquidityShare = (
  liquidityUsd: number | undefined,
  marketCapUsd: number | undefined,
): number | undefined => {
  if (
    liquidityUsd === undefined ||
    marketCapUsd === undefined ||
    !Number.isFinite(liquidityUsd) ||
    !Number.isFinite(marketCapUsd) ||
    marketCapUsd <= 0
  ) {
    return undefined
  }

  return (liquidityUsd / marketCapUsd) * 100
}

/**
 * The same figure, read.
 *
 * Under two percent is where getting out starts to cost more than the move you
 * were trading; over ten is deep for anything at this size. The bands are
 * wide on purpose — this is a warning light, not a measurement.
 */
export const liquidityTone = (share: number | undefined): Tone => {
  if (share === undefined || !Number.isFinite(share)) {
    return 'quiet'
  }
  if (share < 2) {
    return 'bad'
  }
  if (share < 5) {
    return 'watch'
  }
  return 'good'
}

/**
 * What one holder is worth on average, which says how concentrated it is.
 *
 * Nothing for no holders — a token nobody holds has no average, and a zero
 * there reads as a token held by people with nothing.
 */
export const perHolderUsd = (
  marketCapUsd: number | undefined,
  holders: number | undefined,
): number | undefined =>
  marketCapUsd === undefined ||
  holders === undefined ||
  !Number.isFinite(marketCapUsd) ||
  !holders ||
  holders <= 0
    ? undefined
    : marketCapUsd / holders

/**
 * How far off its best the price is now.
 *
 * Negative is below the high, which is where a token usually is. Nothing when
 * there is no high to measure against, rather than a nought that would read as
 * "at its peak".
 */
export const offHigh = (
  marketCapUsd: number | undefined,
  athMarketCapUsd: number | undefined,
): number | undefined => {
  if (
    marketCapUsd === undefined ||
    athMarketCapUsd === undefined ||
    !Number.isFinite(marketCapUsd) ||
    !Number.isFinite(athMarketCapUsd) ||
    athMarketCapUsd <= 0
  ) {
    return undefined
  }

  return ((marketCapUsd - athMarketCapUsd) / athMarketCapUsd) * 100
}
